import { Cell } from '@telegram-apps/telegram-ui'
import { CornerDownLeft } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { useDevToolsStore } from 'shared/store/devtools'

/**
 * Navigate between pages without a URL bar. Suggestions come from the route
 * table; free-typed paths work too (the app uses a HashRouter, so navigation
 * is just an update of location.hash).
 */
export function RouteNavigator({ paths }: { paths: readonly string[] }) {
	const [value, setValue] = useState('')
	const setSheetOpen = useDevToolsStore((s) => s.setSheetOpen)

	const go = (raw: string) => {
		const path = raw.trim()
		if (!path) return
		window.location.hash = `#${path.startsWith('/') ? path : `/${path}`}`
		setSheetOpen(false)
	}

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault()
		go(value)
	}

	const query = value.trim()
	const suggestions = query
		? paths.filter((p) => p.includes(query))
		: [...paths]

	return (
		<div className="space-y-2">
			<h2 className="text-xl font-semibold">Navigate</h2>
			<form className="flex gap-2" onSubmit={handleSubmit}>
				<Input
					value={value}
					onChange={(e) => setValue(e.target.value)}
					placeholder="/admin/lots/123"
					autoCapitalize="none"
					autoCorrect="off"
					spellCheck={false}
				/>
				<Button type="submit" disabled={!query}>
					<CornerDownLeft className="size-4" />
				</Button>
			</form>
			<div className="max-h-48 overflow-y-auto rounded-md border border-[var(--tgui--outline,rgba(255,255,255,0.1))]">
				{suggestions.map((path) => (
					<Cell key={path} onClick={() => go(path)}>
						<span className="font-mono text-sm">{path}</span>
					</Cell>
				))}
				{suggestions.length === 0 && (
					<p className="p-3 text-sm text-[var(--tgui--hint_color,rgb(170,170,170))]">
						No matching route — submit to navigate anyway.
					</p>
				)}
			</div>
		</div>
	)
}
